import React, { useEffect, useRef, useState } from "react";

export default function ProgressBar({
    value = 0,
    onChange,
    onChangeEnd,
    rtl = true,
    alwaysShowTooltip = false,
    tooltipFormatter,
    color = "#0d6efd",
    background = "#d9d9d9",
    height = 6,
    hoverHeight = 10
}) {

    const barRef = useRef(null);

    const [progress, setProgress] = useState(value);
    const [dragging, setDragging] = useState(false);
    const [hover, setHover] = useState(false);

    const progressRef = useRef(value);

    useEffect(() => {
        progressRef.current = progress;
    }, [progress]);

    useEffect(() => {
        if (!dragging)
            setProgress(value);
    }, [value, dragging]);

    const getClientX = (e) => {
        if (e.touches && e.touches.length)
            return e.touches[0].clientX;

        if (e.changedTouches && e.changedTouches.length)
            return e.changedTouches[0].clientX;

        return e.clientX;
    };

    const updateProgress = (clientX) => {

        if (!barRef.current)
            return;

        const rect = barRef.current.getBoundingClientRect();

        let percent = rtl
            ? ((rect.right - clientX) / rect.width) * 100
            : ((clientX - rect.left) / rect.width) * 100;

        percent = Math.max(0, Math.min(100, percent));

        progressRef.current = percent;
        setProgress(percent);

        onChange?.(percent);
    };

    const handlePointerDown = (e) => {
        e.preventDefault();
        setDragging(true);
        updateProgress(getClientX(e));
    };

    useEffect(() => {

        const handleMove = (e) => {
            if (!dragging) return;
            updateProgress(getClientX(e));
        };

        const handleUp = () => {
            if (!dragging) return;

            setDragging(false);
            onChangeEnd?.(progressRef.current);
        };

        window.addEventListener("mousemove", handleMove);
        window.addEventListener("mouseup", handleUp);

        window.addEventListener("touchmove", handleMove, { passive: false });
        window.addEventListener("touchend", handleUp);
        window.addEventListener("touchcancel", handleUp);

        return () => {
            window.removeEventListener("mousemove", handleMove);
            window.removeEventListener("mouseup", handleUp);

            window.removeEventListener("touchmove", handleMove);
            window.removeEventListener("touchend", handleUp);
            window.removeEventListener("touchcancel", handleUp);
        };

    }, [dragging, onChangeEnd]);

    const currentHeight = hover || dragging ? hoverHeight : height;

    const tooltipVisible = alwaysShowTooltip || dragging || hover;

    const formatTooltip = (v) =>
        tooltipFormatter ? tooltipFormatter(v) : `${Math.round(v)}%`;

    return (
        <div
            ref={barRef}
            onMouseDown={handlePointerDown}
            onTouchStart={handlePointerDown}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                position: "relative",
                width: "100%",
                height: currentHeight,
                background,
                borderRadius: currentHeight,
                cursor: dragging ? "grabbing" : "pointer",
                transition: "height .18s ease",
                userSelect: "none",
                touchAction: "none"
            }}
        >

            {/* Progress */}
            <div
                style={{
                    position: "absolute",
                    [rtl ? "right" : "left"]: 0,
                    width: `${progress}%`,
                    height: "100%",
                    background: color,
                    borderRadius: currentHeight,
                    transition: dragging ? "none" : "width .15s ease"
                }}
            />

            {/* Thumb */}
            <div
                style={{
                    position: "absolute",
                    [rtl ? "right" : "left"]:
                        `calc(${progress}% - ${(dragging || hover) ? 9 : 7}px)`,
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none"
                }}
            >

                {/* Tooltip */}
                <div
                    style={{
                        position: "absolute",
                        bottom: 23,
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: "#212529",
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 500,
                        padding: "3px 8px",
                        borderRadius: 5,
                        whiteSpace: "nowrap",
                        boxShadow: "0 2px 8px rgba(0,0,0,.25)",
                        opacity: tooltipVisible ? 1 : 0,
                        transition: "opacity .18s ease",
                        userSelect: "none"
                    }}
                >
                    {formatTooltip(progress)}
                </div>

                {/* Thumb circle */}
                <div
                    style={{
                        width: dragging || hover ? 18 : 14,
                        height: dragging || hover ? 18 : 14,
                        borderRadius: "50%",
                        background: color,
                        border: "2px solid #fff",
                        cursor: dragging ? "grabbing" : "grab",
                        boxShadow: dragging
                            ? "0 0 0 6px rgba(13,110,253,.18), 0 3px 10px rgba(0,0,0,.35)"
                            : hover
                                ? "0 0 0 4px rgba(13,110,253,.12), 0 2px 6px rgba(0,0,0,.25)"
                                : "0 2px 6px rgba(0,0,0,.25)",
                        transition: "all .18s ease",
                        pointerEvents: "auto"
                    }}
                />
            </div>

        </div>
    );
}